import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ShoppingBag, Plus, Minus, Loader2, MessageCircle, ShieldCheck, Sparkles } from "lucide-react";
import { money } from "@/lib/shopify";
import { waUrl } from "@/lib/brand";
import { CheckItem } from "./Sections";

export const ProductShowcase = ({ config, shop }) => {
  const { product, loading, error, qty, setQty, buying, available } = shop;
  const [imgIdx, setImgIdx] = useState(0);
  const images = product?.images?.length ? product.images : product?.featuredImage ? [product.featuredImage] : [];
  const img = images[imgIdx] || images[0];
  const crossLink = config.key === "ghee"
    ? { to: "/forest-honey", label: "Pure Forest Honey 500g" }
    : { to: "/a2cow-ghee", label: "Pure A2 Cow Ghee 500g" };

  return (
    <section id="buy" className="py-14 sm:py-20 lg:py-28 bg-[#FBF4E1] scroll-mt-20" data-testid="landing-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="chip mb-4"><Sparkles className="w-3.5 h-3.5" /> {config.showcase.kicker}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#143C18]">
            Order Rudrima <span className="italic font-serif-alt text-[#D4A017]">{config.shortName}</span>
          </h2>
        </div>

        <div className="card-premium overflow-hidden !transform-none" data-testid={`landing-${config.key}-card`}>
          <div className="grid lg:grid-cols-2">
            <div className="relative bg-gradient-to-br from-[#FFF8E7] to-[#F6EBCB] p-6 lg:p-10 flex flex-col items-center justify-center min-h-[340px]">
              {loading ? (
                <div className="w-full max-w-[420px] aspect-square rounded-2xl bg-[#F6EBCB] animate-pulse" />
              ) : img ? (
                <>
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={img.url}
                      src={img.url}
                      alt={img.altText || `${config.shopifyTitle} — Rudrima`}
                      width={img.width || 800}
                      height={img.height || 800}
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.45 }}
                      className="w-full max-w-[420px] rounded-2xl object-cover shadow-[0_25px_50px_-15px_rgba(20,60,24,0.35)]"
                      loading="lazy"
                      decoding="async"
                      data-testid="showcase-image"
                    />
                  </AnimatePresence>
                  {images.length > 1 && (
                    <div className="mt-4 flex gap-2 flex-wrap justify-center" data-testid="showcase-thumbs">
                      {images.map((im, i) => (
                        <button key={im.url} onClick={() => setImgIdx(i)} aria-label={`View image ${i + 1}`} className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition ${i === imgIdx ? "border-[#2E7D32]" : "border-transparent opacity-70 hover:opacity-100"}`}>
                          <img src={im.url} alt="" width="56" height="56" className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-[#8D6E63] text-sm">{error || "Product image unavailable"}</div>
              )}
              <div className="absolute top-5 left-5 chip chip-gold shadow-sm"><Sparkles className="w-3 h-3" /> {config.weight} Pack</div>
            </div>

            <div className="p-6 lg:p-10 flex flex-col justify-center">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8D6E63] font-bold mb-3">Rudrima · Live Shopify Product</div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#143C18] leading-tight" data-testid="showcase-title">
                {product?.title || config.shopifyTitle}
              </h3>
              {product?.description && <p className="mt-3 text-[#5D4037] leading-relaxed line-clamp-4" data-testid="showcase-description">{product.description}</p>}
              {error && <p className="mt-3 text-sm text-[#B8860B]" data-testid="showcase-error">Live product data is temporarily unavailable. {error}</p>}

              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {config.showcase.highlights.map((h) => <CheckItem key={h}>{h}</CheckItem>)}
              </ul>

              <div className="mt-5 flex items-center gap-2 text-sm flex-wrap" data-testid="showcase-availability">
                {loading ? <span className="text-[#8D6E63]">Checking availability…</span> : available ? (
                  <span className="inline-flex items-center gap-1.5 text-[#1F5D25] font-semibold"><span className="w-2 h-2 rounded-full bg-[#2E7D32] animate-pulse" /> In Stock — Ready to ship</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[#8D6E63] font-semibold"><span className="w-2 h-2 rounded-full bg-[#8D6E63]" /> Out of Stock</span>
                )}
                <span className="text-[#8D6E63]">·</span>
                <span className="text-[#8D6E63] inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#2E7D32]" /> Secure Shopify Checkout</span>
              </div>

              <div className="mt-6 flex flex-wrap items-end justify-between gap-4 pt-6 border-t border-[#8D6E63]/15">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#8D6E63] font-bold">{config.weight} · Price</div>
                  <div className="font-display text-3xl font-bold text-[#143C18] flex items-baseline gap-3" data-testid="showcase-price">
                    {shop.priceLabel}
                    {product?.compareAtPrice && Number(product.compareAtPrice.amount) > shop.price && (
                      <span className="text-base font-body font-medium text-[#8D6E63] line-through">{money(product.compareAtPrice.amount, product.compareAtPrice.currencyCode)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2" data-testid="showcase-qty">
                  <span className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold">Qty</span>
                  <div className="flex items-center bg-white border border-[#8D6E63]/20 rounded-full overflow-hidden">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} disabled={qty <= 1} aria-label="Decrease quantity" data-testid="showcase-qty-dec" className="w-11 h-11 flex items-center justify-center hover:bg-[#F6EBCB] disabled:opacity-40 transition"><Minus className="w-4 h-4 text-[#143C18]" /></button>
                    <span className="w-10 text-center font-bold text-[#143C18] tabular-nums" data-testid="showcase-qty-value">{qty}</span>
                    <button onClick={() => setQty(Math.min(20, qty + 1))} aria-label="Increase quantity" data-testid="showcase-qty-inc" className="w-11 h-11 flex items-center justify-center hover:bg-[#F6EBCB] transition"><Plus className="w-4 h-4 text-[#143C18]" /></button>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => shop.buyNow(qty)} disabled={!available || buying || loading} data-testid="showcase-buy-now-btn" className="btn-primary !py-3.5 justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                  {buying ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><ShoppingBag className="w-4 h-4" /> Buy Now · {shop.totalLabel}</>}
                </button>
                <button onClick={shop.addToCart} disabled={!available || buying || loading} data-testid="showcase-add-to-cart-btn" className="btn-outline !py-3.5 justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                  <Plus className="w-4 h-4" /> Add to Cart
                </button>
              </div>
              {!loading && !available && (
                <a href={waUrl(config.showcase.notifyMsg)} target="_blank" rel="noopener noreferrer" data-testid="showcase-notify-btn" className="mt-3 btn-outline !py-3 !border-[#25D366] !text-[#0F5132] hover:!bg-[#25D366] hover:!text-white justify-center">
                  <MessageCircle className="w-4 h-4" /> Notify me on WhatsApp
                </a>
              )}
              <p className="mt-4 text-xs text-[#8D6E63]">
                Checkout, payments and order updates are handled securely by Shopify. Also see our{" "}
                <Link to="/shipping-policy" className="underline hover:text-[#2E7D32]">Shipping</Link> and{" "}
                <Link to="/refund-and-return-policy" className="underline hover:text-[#2E7D32]">Refund</Link> policies · Pair it with{" "}
                <Link to={crossLink.to} className="underline hover:text-[#2E7D32]" data-testid="showcase-cross-link">{crossLink.label}</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
